import { Box, Grid, TextLink, Typography } from "@drone-js/core";
import { OneOnboardingImages } from "libs/images/oneOnboarding";
import type { ReactNode } from "react";
import { usePropertySelectionViewModelValue } from "../propertySelectionViewModelAtom";
import { CardSelector } from "./CardSelector";
import { CheckedItem } from "./CheckedItem";
import { PaintedDoorModal } from "./PaintedDoorModal";
import { ImportOptions } from "./constants";
import { usePaintedDoorModalOpenState } from "./paintedDoorModalOpenAtom";

const buildFlexibleDescription = (template: string | null, platforms: string | null): ReactNode => {
    const [before = "", after = ""] = (template ?? "").split("{0}");
    return (
        <>
            {before}
            {platforms != null && <Typography variant="body-default-strong">{platforms}</Typography>}
            {after}
        </>
    );
};

export const AutomaticMethod = () => {
    const {
        importMethodCms: {
            speedDescription,
            secure,
            bComFirstSelectionText,
            flexible,
            flexibleDescriptionTemplate,
            flexibleDescriptionPlatforms,
            flexibleDescriptionPlatformsWithRakuten,
            fastTrackImport,
            quick,
            bComSecureDescription,
            notOnboarding,
            clickHere,
        },
        showRakutenImport,
    } = usePropertySelectionViewModelValue();

    const platforms = showRakutenImport ? flexibleDescriptionPlatformsWithRakuten : flexibleDescriptionPlatforms;

    const flexibleDescriptionNode = buildFlexibleDescription(flexibleDescriptionTemplate, platforms);

    const [modal, setModal] = usePaintedDoorModalOpenState();

    const handleOpenModal = () => setModal(true);
    const handleCloseModal = () => setModal(false);

    const logos = [
        ...(showRakutenImport ? [OneOnboardingImages.ImportMethodAutomaticRakuten] : []),
        OneOnboardingImages.ImportMethodAutomaticAirbnb,
        OneOnboardingImages.ImportMethodAutomaticBCom,
    ];

    return (
        <CardSelector
            id={ImportOptions.Automatic}
            title={bComFirstSelectionText}
            logo={logos}
            jacket={{
                title: fastTrackImport,
                size: "m",
                leadingIcon: "fill.symbol.lightning2",
                variant: "secondary.notice",
                position: "above",
            }}
            iconSize="48px"
            bottom={
                <Box display="flex" flexWrap="wrap" alignItems="center">
                    <Typography variant="body-s" me="8" textColor="base.subtle">
                        {notOnboarding}
                    </Typography>
                    <Box cursor="pointer">
                        <TextLink
                            hierarchy="primary"
                            label={clickHere}
                            onClick={handleOpenModal}
                            data-element-name="import-from-another-platform-link"
                        />
                    </Box>
                </Box>
            }
            data-element-name={showRakutenImport ? "import-from-rakuten-card" : "import-from-airbnb-card"}
        >
            <Box mt="24">
                <Grid spacing="12">
                    <CheckedItem title={flexible} description={flexibleDescriptionNode} />
                    <CheckedItem title={quick} description={speedDescription} />
                    <CheckedItem title={secure} description={bComSecureDescription} />
                </Grid>
            </Box>
            <PaintedDoorModal open={modal} onClose={handleCloseModal} />
        </CardSelector>
    );
};
